import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { DroneSchema, FAMILIES, type Drone, type SourceLayer } from '@tonist/shared';
import { PageShell } from '../components/PageShell';
import { AuditionStrip } from '../components/editor/AuditionStrip';
import { SourceEditor } from '../components/editor/SourceEditor';
import { MacroDials } from '../components/editor/MacroDials';
import { EngineForm, type DraftObject } from '../components/editor/EngineForm';
import { setPath } from '../components/editor/fieldSpec';
import { FAMILY_LABEL } from '../components/FamilyDot';
import { getEngine } from '../engine';
import { engineStarted, onEngineStarted, type EngineWithExtras } from '../lib/engineBoot';
import { errorMessage } from '../lib/api';
import { useDrones, copyOf, FALLBACK_DRONE } from '../store/dronesStore';
import { useSettings, debounced } from '../store/settingsStore';
import { useAuth } from '../store/authStore';
import { toast } from '../store/uiStore';
import { tuningFromSettings } from '../music/tuning';

/** Keyed by :id so moving between two drones resets the draft. */
export function DroneEditor() {
  const { id } = useParams();
  return <DroneEditorInner key={id ?? 'new'} />;
}

function DroneEditorInner() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const engine = useMemo<EngineWithExtras>(() => getEngine(), []);
  const presets = useDrones((s) => s.presets);
  const mine = useDrones((s) => s.mine);
  const user = useAuth((s) => s.user);
  const tonicPc = useSettings((s) => s.tonicPc), scaleId = useSettings((s) => s.scaleId), ji = useSettings((s) => s.ji);
  const tuning = useMemo(() => tuningFromSettings({ tonicPc, scaleId, ji }), [tonicPc, scaleId, ji]);

  const record = id ? mine.find((r) => r.id === id) : undefined;
  const preset = id ? presets.find((p) => p.id === id) : undefined;
  const isMine = !!record;
  const missing = !!id && !record && !preset;

  const [draft, setDraft] = useState<DraftObject>(() => {
    if (record) return structuredClone(record.drone) as unknown as DraftObject;
    if (preset) return copyOf(preset, preset.name) as unknown as DraftObject;
    const from = params.get('from');
    const base = (from && (presets.find((p) => p.id === from) ?? mine.find((r) => r.id === from)?.drone)) || presets[0] || FALLBACK_DRONE;
    return copyOf(base, from ? `${base.name} copy` : 'New drone') as unknown as DraftObject;
  });
  const parsed = useMemo(() => DroneSchema.safeParse(draft), [draft]);
  const errors = useMemo<Record<string, string>>(() => {
    if (parsed.success) return {};
    const out: Record<string, string> = {};
    for (const issue of parsed.error.issues) out[issue.path.join('.')] = issue.message;
    return out;
  }, [parsed]);
  const valid: Drone | null = parsed.success ? parsed.data : null;

  // live: every valid change → store (if mine) + engine (debounced 50 ms)
  const [ready, setReady] = useState(false);
  const [started, setStarted] = useState(engineStarted());
  useEffect(() => onEngineStarted(() => setStarted(true)), []);
  const loadedId = useRef<string | null>(null);
  const live = useMemo(() => debounced<Drone>((d) => {
    if (isMine) void useDrones.getState().update(d.id, d).catch((e: unknown) => toast(errorMessage(e), 'error'));
    if (!engineStarted()) return;
    if (loadedId.current !== d.id) {
      loadedId.current = d.id;
      setReady(false);
      engine.loadDrone(d).then(() => setReady(true)).catch((e: unknown) => toast(errorMessage(e), 'error'));
    } else engine.updateDrone?.(d);
  }, 50), [engine, isMine]);
  const first = useRef(true);
  useEffect(() => {
    if (!valid) return;
    if (first.current) { first.current = false; if (!started) return; }
    live.call(valid);
  }, [valid, live, started]);
  useEffect(() => () => live.flush(), [live]);

  const set = (path: string, value: unknown) => setDraft((d) => setPath(d, path, value));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!valid) { toast('Fix the highlighted fields first', 'error'); return; }
    setBusy(true);
    try {
      if (isMine) { live.flush(); await useDrones.getState().update(valid.id, valid); toast('Saved'); }
      else { const rec = await useDrones.getState().create(valid); toast('Saved to my drones'); navigate(`/drones/${rec.id}`, { replace: true }); }
    } catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(false); }
  };
  const duplicate = async () => {
    if (!valid) return;
    setBusy(true);
    try { const rec = await useDrones.getState().create(copyOf(valid)); navigate(`/drones/${rec.id}`); } catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!record || !confirm(`Delete “${record.drone.name}”?`)) return;
    try { await useDrones.getState().remove(record.id); navigate('/drones'); } catch (e) { toast(errorMessage(e), 'error'); }
  };
  const publish = async () => {
    if (!record) return;
    setBusy(true);
    try { const r = await useDrones.getState().publish(record.id, !record.isPublic); toast(r.isPublic ? 'Published' : 'Unpublished'); } catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(false); }
  };
  const playIt = () => { if (!valid) return; if (!isMine) { void save(); } useSettings.getState().setDroneIds([valid.id]); navigate('/'); };

  if (missing) {
    return <PageShell title="No such drone"><div className="notice">That drone isn’t in your list. <Link to="/drones">Back to drones</Link>.</div></PageShell>;
  }

  const shareUrl = record?.isPublic && record.slug ? `${location.origin}/d/${record.slug}` : null;
  return (
    <PageShell eyebrow={isMine ? 'Editing' : preset ? 'Preset (saving makes your own copy)' : 'New drone'} title={String(draft.name ?? '')}
      actions={<>
        <button type="button" className="btn btn--primary" disabled={busy || !valid} onClick={() => void save()}>{isMine ? 'Save' : 'Save to my drones'}</button>
        <button type="button" className="btn" disabled={busy || !valid} onClick={playIt}>Play on the mandala</button>
        {isMine && <button type="button" className="btn" disabled={busy} onClick={() => void duplicate()}>Duplicate</button>}
        {isMine && (user ? <button type="button" className="btn" disabled={busy} onClick={() => void publish()}>{record?.isPublic ? 'Unpublish' : 'Publish'}</button> : <Link to="/auth" className="btn">Sign in to publish</Link>)}
        {isMine && <button type="button" className="btn btn--danger" disabled={busy} onClick={() => void remove()}>Delete</button>}
      </>}>
      {shareUrl && <div className="notice notice--ok row">Public at <a href={shareUrl}>{shareUrl}</a><button type="button" className="btn btn--sm" onClick={() => navigator.clipboard?.writeText(shareUrl).then(() => toast('Link copied'))}>Copy</button></div>}
      {errors[''] && <div className="notice notice--err">{errors['']}</div>}
      <div className="two-col">
        <div className="glass panel sticky stack">
          <AuditionStrip engine={engine} drone={valid} tuning={tuning} ready={ready || !started} />
          {!started && <div className="hint">Audio starts on your first touch of a pad.</div>}
        </div>
        <div className="stack" style={{ gap: 20 }}>
          <div className="glass panel stack">
            <div className="form-grid">
              <div className="field" style={{ gridColumn: '1 / -1' }}>
                <label htmlFor="drone-name">Name</label>
                <input id="drone-name" className="input" value={String(draft.name ?? '')} maxLength={60} onChange={(e) => set('name', e.target.value)} />
                {errors.name && <span className="error">{errors.name}</span>}
              </div>
              <div className="field" style={{ gridColumn: '1 / -1' }}>
                <label htmlFor="drone-desc">Description</label>
                <textarea id="drone-desc" className="textarea" value={String(draft.description ?? '')} maxLength={400} onChange={(e) => set('description', e.target.value)} />
                {errors.description && <span className="error">{errors.description}</span>}
              </div>
              <div className="field">
                <label htmlFor="drone-family">Family colour</label>
                <select id="drone-family" className="select" value={String(draft.family ?? 'white')} onChange={(e) => set('family', e.target.value)}>
                  {FAMILIES.map((f) => <option key={f} value={f}>{FAMILY_LABEL[f]}</option>)}
                </select>
              </div>
            </div>
          </div>
          <div className="glass panel stack">
            <h3>Source</h3>
            <SourceEditor engine={engine} sources={(draft.sources as SourceLayer[]) ?? []} errors={errors} onChange={(sources) => set('sources', sources)} />
            {errors.sources && <span className="error">{errors.sources}</span>}
          </div>
          <div className="glass panel stack">
            <h3>Macros</h3>
            <MacroDials draft={draft} onChange={set} />
          </div>
          <div className="glass panel">
            <EngineForm draft={draft} errors={errors} onChange={set} />
          </div>
        </div>
      </div>
    </PageShell>
  );
}
